import {IActionRecordsMap, IDocumentData} from "@buildwithflux/models";
import {areWeTestingWithJest, isDevEnv, Records} from "@buildwithflux/shared";
import {cloneDeep, pickBy} from "lodash";
import {StoreApi, UseBoundStore, create} from "zustand";
import {devtools} from "zustand/middleware";
import {useShallow} from "zustand/react/shallow";

import {useFluxServices} from "injection/hooks";
import {FluxLogger} from "modules/storage_engine/connectors/LogConnector";

import type {
    IChangeHistoryCacheMetadata,
    IChangeHistoryData,
    IChangeHistoryEntriesMap,
    IChangeHistoryListItemData,
} from "./types";

export type {
    IChangeHistoryCacheMetadata,
    IChangeHistoryData,
    IChangeHistoryEntriesMap,
    IChangeHistoryListItemData,
} from "./types";

/**
 * Helper function to find top and bottom action records by date and increment count
 */
function findTopAndBottomActionRecordByDateAndIncrementCount(
    actionRecords: IActionRecordsMap,
    changeHistoryCacheMetadata: IChangeHistoryCacheMetadata,
    changeHistoryEntries: IChangeHistoryEntriesMap,
): IChangeHistoryCacheMetadata {
    // Cheap cloneDeep since it's only 5 scalars
    const result = cloneDeep(changeHistoryCacheMetadata);

    for (const record of Object.values(actionRecords)) {
        if (record.actionTimestamp > result.topActionRecordTimestamp) {
            result.topActionRecordTimestamp = record.actionTimestamp;
            result.topActionRecordUid = record.actionRecordUid;
            result.topActionRecordName = record.actionName;
        }
        if (record.actionTimestamp < result.bottomActionRecordTimestamp) {
            result.bottomActionRecordTimestamp = record.actionTimestamp;
            result.bottomActionRecordUid = record.actionRecordUid;
        }
        if (!changeHistoryEntries[record.actionRecordUid]) {
            ++result.cachedEntriesCount;
        }
    }
    return result;
}

/**
 * State for managing change history data
 *
 * This replaces the Redux changeHistory reducer state, which tracked
 * change history entries, cache metadata, list items, and current/requested change IDs.
 */
type ChangeHistoryStoreState = {
    /**
     * The document UID associated with this change history
     */
    documentUid: string;

    /**
     * Total count of entries
     */
    countOfTotalEntries: number;

    /**
     * Cache metadata for change history entries
     */
    changeHistoryCacheMetadata: IChangeHistoryCacheMetadata;

    /**
     * Map of change history entries by UID
     */
    changeHistoryEntries: IChangeHistoryEntriesMap;

    /**
     * List items for display in the change history panel
     * Optional to match the Redux reducer type
     */
    changeHistoryListItems?: IChangeHistoryListItemData[];

    /**
     * The current change ID being viewed
     */
    currentChangeId: string;

    /**
     * The requested change ID to navigate to
     */
    requestedChangeId: string;

    /**
     * The latest snapshot of the document for restoration
     */
    latestSnapshot: IDocumentData | null;

    /**
     * Adds change history records
     */
    addChangeHistoryRecords: (actionRecords: IActionRecordsMap) => void;

    /**
     * Sets the change history list items
     */
    setChangeHistoryListItems: (items: IChangeHistoryListItemData[]) => void;

    /**
     * Sets the current change ID
     */
    setCurrentChangeId: (currentChangeId: string) => void;

    /**
     * Sets the requested change ID
     */
    setRequestedChangeId: (requestedChangeId: string) => void;

    /**
     * Sets the latest snapshot
     */
    setLatestSnapshot: (latestSnapshot: IDocumentData | null) => void;

    /**
     * Clears all change history
     */
    clearChangeHistory: () => void;

    /**
     * Resets the store to initial state
     */
    reset: () => void;
};

/**
 * The exported type of the store
 */
export type UseChangeHistoryStore = UseBoundStore<StoreApi<ChangeHistoryStoreState>>;

/**
 * The exported type of the raw vanilla store (for non-React access like epics)
 */
export type ChangeHistoryStore = StoreApi<ChangeHistoryStoreState>;

const initialCacheMetadata: IChangeHistoryCacheMetadata = {
    bottomActionRecordUid: "",
    bottomActionRecordTimestamp: Number.MAX_SAFE_INTEGER,
    topActionRecordUid: "",
    topActionRecordName: "",
    topActionRecordTimestamp: 0,
    cachedEntriesCount: 0,
};

const initialState: IChangeHistoryData = {
    countOfTotalEntries: 0,
    documentUid: "",
    changeHistoryCacheMetadata: initialCacheMetadata,
    changeHistoryEntries: {},
    changeHistoryListItems: [],
    currentChangeId: "",
    requestedChangeId: "",
    latestSnapshot: null,
};

/**
 * Creates the vanilla non-bound-to-React change history store
 *
 * This is a vanilla Zustand store, and is only exported for use in tests. In components, you'd usually use the hooks
 * below, and even when trying to access low-level details of the store, you'd use the bound version of this store.
 *
 * The store returned from this function cannot be directly used as a hook in a component. For that, see
 * `createChangeHistoryStoreAndHook` below.
 */
export function createChangeHistoryStore(): UseChangeHistoryStore {
    return create<ChangeHistoryStoreState>()(
        devtools(
            (set, get): ChangeHistoryStoreState => {
                return {
                    ...initialState,

                    addChangeHistoryRecords: (actionRecords: IActionRecordsMap) => {
                        if (Records.isEmpty(actionRecords)) {
                            FluxLogger.captureError(
                                new Error("addChangeHistoryRecords called with empty actionRecords"),
                            );
                            return;
                        }

                        // NOTE: key line to prevent permission actions like setRole from
                        // being in change history.
                        const filtered = pickBy(actionRecords, (record) => record.actionUpdatesDocumentVersion);

                        set(
                            (state) => {
                                const newCacheMetadata = findTopAndBottomActionRecordByDateAndIncrementCount(
                                    filtered,
                                    state.changeHistoryCacheMetadata,
                                    state.changeHistoryEntries,
                                );
                                return {
                                    changeHistoryCacheMetadata: newCacheMetadata,
                                    changeHistoryEntries: {...state.changeHistoryEntries, ...filtered},
                                };
                            },
                            false,
                            "addChangeHistoryRecords",
                        );
                    },

                    setChangeHistoryListItems: (items: IChangeHistoryListItemData[]) => {
                        set({changeHistoryListItems: items}, false, "setChangeHistoryListItems");
                    },

                    setCurrentChangeId: (currentChangeId: string) => {
                        set(
                            (state) => {
                                if (currentChangeId === "") {
                                    return {currentChangeId, latestSnapshot: null};
                                }
                                return {currentChangeId};
                            },
                            false,
                            "setCurrentChangeId",
                        );
                    },

                    setRequestedChangeId: (requestedChangeId: string) => {
                        // NOTE: see useChangeHistory.ts for useLayoutEffect that is
                        // triggered by requestedChangeId
                        const state = get();
                        if (requestedChangeId === state.changeHistoryCacheMetadata.topActionRecordUid) {
                            set({requestedChangeId, latestSnapshot: null}, false, "setRequestedChangeId");
                        } else {
                            set({requestedChangeId}, false, "setRequestedChangeId");
                        }
                    },

                    setLatestSnapshot: (latestSnapshot: IDocumentData | null) => {
                        set({latestSnapshot}, false, "setLatestSnapshot");
                    },

                    clearChangeHistory: () => {
                        set(initialState, false, "clearChangeHistory");
                    },

                    reset: () => {
                        set(initialState, false, "reset");
                    },
                };
            },
            {enabled: isDevEnv() && !areWeTestingWithJest(), name: "ChangeHistoryStore"},
        ),
    );
}

/**
 * Creates both the raw store and the bound hook from the same store instance
 *
 * This is used by the service container to expose both the raw store (for epics) and the hook (for React components)
 */
export function createChangeHistoryStoreAndHook(): {store: ChangeHistoryStore; hook: UseChangeHistoryStore} {
    const store = createChangeHistoryStore();
    return {store, hook: store};
}

// ============================================================================
// React Hooks for accessing change history store state
// ============================================================================

/**
 * Gets a reactive slice of the change history store: the full state
 */
export function useChangeHistoryState(): IChangeHistoryData {
    return useFluxServices().useChangeHistoryStore(
        useShallow((state) => ({
            documentUid: state.documentUid,
            countOfTotalEntries: state.countOfTotalEntries,
            changeHistoryCacheMetadata: state.changeHistoryCacheMetadata,
            changeHistoryEntries: state.changeHistoryEntries,
            changeHistoryListItems: state.changeHistoryListItems,
            currentChangeId: state.currentChangeId,
            requestedChangeId: state.requestedChangeId,
            latestSnapshot: state.latestSnapshot,
        })),
    );
}

/**
 * Gets a reactive slice of the change history store: the change history list items
 */
export function useChangeHistoryList(): IChangeHistoryListItemData[] | undefined {
    return useFluxServices().useChangeHistoryStore(useShallow((state) => state.changeHistoryListItems));
}

/**
 * Gets a reactive slice of the change history store: the current change ID
 */
export function useCurrentChangeId(): string {
    return useFluxServices().useChangeHistoryStore((state) => state.currentChangeId);
}

/**
 * Gets a reactive slice of the change history store: the requested change ID
 */
export function useRequestedChangeId(): string {
    return useFluxServices().useChangeHistoryStore((state) => state.requestedChangeId);
}

/**
 * Gets a reactive slice of the change history store: the top change ID
 */
export function useTopChangeId(): string {
    return useFluxServices().useChangeHistoryStore((state) => state.changeHistoryCacheMetadata.topActionRecordUid);
}

/**
 * Gets a reactive slice of the change history store: the change history entries
 */
export function useChangeHistoryEntries(): IChangeHistoryEntriesMap {
    return useFluxServices().useChangeHistoryStore(useShallow((state) => state.changeHistoryEntries));
}

/**
 * Gets a reactive slice of the change history store: the cache metadata
 */
export function useChangeHistoryCacheMetadata(): IChangeHistoryCacheMetadata {
    return useFluxServices().useChangeHistoryStore(useShallow((state) => state.changeHistoryCacheMetadata));
}

/**
 * Gets a reactive slice of the change history store: the latest snapshot
 */
export function useLatestSnapshot(): IDocumentData | null {
    return useFluxServices().useChangeHistoryStore((state) => state.latestSnapshot);
}

// ============================================================================
// React Hooks for accessing change history store actions
// ============================================================================

/**
 * Returns the function to add change history records
 */
export function useAddChangeHistoryRecords(): (actionRecords: IActionRecordsMap) => void {
    return useFluxServices().useChangeHistoryStore((state) => state.addChangeHistoryRecords);
}

/**
 * Returns the function to set change history list items
 */
export function useSetChangeHistoryListItems(): (items: IChangeHistoryListItemData[]) => void {
    return useFluxServices().useChangeHistoryStore((state) => state.setChangeHistoryListItems);
}

/**
 * Returns the function to set the current change ID
 */
export function useSetCurrentChangeId(): (currentChangeId: string) => void {
    return useFluxServices().useChangeHistoryStore((state) => state.setCurrentChangeId);
}

/**
 * Returns the function to set the requested change ID
 */
export function useSetRequestedChangeId(): (requestedChangeId: string) => void {
    return useFluxServices().useChangeHistoryStore((state) => state.setRequestedChangeId);
}

/**
 * Returns the function to set the latest snapshot
 */
export function useSetLatestSnapshot(): (latestSnapshot: IDocumentData | null) => void {
    return useFluxServices().useChangeHistoryStore((state) => state.setLatestSnapshot);
}

/**
 * Returns the function to clear change history
 */
export function useClearChangeHistory(): () => void {
    return useFluxServices().useChangeHistoryStore((state) => state.clearChangeHistory);
}
